import { PageWrapper } from '../../components/PageWrapper';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HOME, PHONE_PASSWORD_FORGOT } from '../../constants/routes';
import { submitPasswordForLogin, handleForgotPasswordFlow } from '../../utils/phoneNumber';
import { auth } from '../../firebase/firebaseConfig';
import useAxios from '../../axios/useAxios';
import OtpInput from 'react18-input-otp';
import { BeatLoader } from 'react-spinners';

export const PhonePasswordScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const axiosInstance = useAxios();
  const { cartItems } = useSelector((state) => state.cart);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [lockUntil, setLockUntil] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { phoneNumber, userExists } = location.state || {};
  const countdownInterval = useRef(null);

  useEffect(() => {
    if (!phoneNumber) {
      navigate(HOME);
    }
  }, [phoneNumber, navigate]);

  // Sync lockUntil from localStorage (set by phoneNumberCheck before arriving here)
  useEffect(() => {
    const storedLock = localStorage.getItem('lockUntil');
    if (storedLock && new Date(storedLock) > new Date()) {
      setLockUntil(storedLock);
    } else {
      localStorage.removeItem('lockUntil');
      setLockUntil(null);
    }
  }, []);

  const formatCountdown = useCallback((lockDate) => {
    const now = new Date();
    const end = new Date(lockDate);
    const diff = Math.max(0, Math.floor((end - now) / 1000));

    if (diff <= 0) return null;

    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;

    if (minutes > 0) {
      return `${minutes}min ${seconds.toString().padStart(2, '0')}s`;
    }
    return `${seconds}s`;
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }

    if (!lockUntil) {
      setCountdown('');
      return;
    }

    const tick = () => {
      const remaining = formatCountdown(lockUntil);
      if (remaining) {
        setCountdown(remaining);
      } else {
        setCountdown('');
        setLockUntil(null);
        setPasswordError('');
        localStorage.removeItem('lockUntil');
        clearInterval(countdownInterval.current);
        countdownInterval.current = null;
      }
    };

    tick();
    countdownInterval.current = setInterval(tick, 1000);

    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [lockUntil, formatCountdown]);

  const isLocked = !!lockUntil && !!countdown;

  const handleSubmitPassword = async () => {
    if (password.length !== 6 || isLocked) return;
    setLoadingMessage('Connexion en cours...');
    setLoading(true);
    const success = await submitPasswordForLogin({
      password,
      confirmPassword: password,
      isSignup: !userExists,
      phoneNumber,
      setLoading,
      setPassword,
      auth,
      dispatch,
      axiosInstance,
      cartItems,
      navigate,
      userResult: auth.currentUser,
      setLockUntil,
      setError: setPasswordError,
    });

    if (!success) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (password.length === 6) {
      handleSubmitPassword();
    }
  }, [password]);

  const handleForgotPassword = async () => {
    if (!phoneNumber) return;

    setLoadingMessage('Envoi du code...');
    setLoading(true);

    const result = await handleForgotPasswordFlow({
      phoneNumber,
      auth,
      axiosInstance,
      setLoading,
      recaptchaContainerId: 'forgot-password-recaptcha',
    });

    if (result.success) {
      navigate(PHONE_PASSWORD_FORGOT, {
        state: { phoneNumber: result.phoneNumber, userExists },
      });
    }
    setLoading(false);
  };

  return (
    <PageWrapper
      title={userExists ? "🔑 Entre ton mot de passe" : "🔑 Crée ton mot de passe"}
      onGoBack={() => navigate(HOME)}
    >
      <div className="flex flex-col items-center justify-center gap-4">
        {loading ? (
          <div className="flex flex-col items-center gap-4">
            <BeatLoader color="#183153" size={20} />
            <p className="text-gray-500 text-lg">{loadingMessage}</p>
          </div>
        ) : (
          <OtpInput
            value={password}
            onChange={setPassword}
            numInputs={6}
            isInputNum={true}
            isDisabled={isLocked}
            shouldAutoFocus={!isLocked}
            inputStyle="password-input"
            isInputSecure={true}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            separator={<span className="otp-separator" />}
            containerStyle="otp-container"
          />
        )}

        {!loading && isLocked && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 w-full">
            <p className="text-red-600 text-lg font-semibold text-center">
              Trop de tentatives incorrectes
            </p>
            <p className="text-red-500 text-center mt-1">
              Réessaie dans <span className="font-semibold">{countdown}</span>
            </p>
          </div>
        )}

        {!loading && !isLocked && passwordError && (
          <p className="text-red-500 text-lg mt-2">{passwordError}</p>
        )}

        {!loading && userExists && (
          <button
            type="button"
            className="geomanistMedium -mt-4 text-2xl p-6 underline rounded-full w-full text-gray-500"
            onClick={handleForgotPassword}
          >
            J'ai oublié mon mot de passe
          </button>
        )}
      </div>

      {/* Hidden reCAPTCHA container */}
      <div id="forgot-password-recaptcha" style={{ display: 'none' }} />
    </PageWrapper>
  );
};
